/**
 * Ilustraciones de las promociones, en SVG inline.
 *
 * <p><strong>Inline y no ficheros:</strong> van en el bundle, asi que no hay una peticion de red por
 * imagen, no hay dominio que anadir a la CSP y es imposible que una promocion deje el hueco en blanco
 * porque un CDN tardo. En una pagina de pago esas tres cosas importan mas que la comodidad de cambiar
 * un PNG.
 *
 * <p><strong>Por que heredan el color de marca:</strong> los degradados se construyen sobre
 * `--apipay-color-primary`, la misma variable que puede sobreescribir el tema del tenant. Asi la
 * publicidad no parece pegada encima del checkout: se ve del mismo color que el resto. `color-mix()`
 * deriva los tonos sin que haya que declararlos uno a uno, y si el navegador no lo soporta cae al
 * color plano, que sigue siendo correcto.
 *
 * <p>Son de RELLENO, para la demo. No nombran comercios ni prometen precios reales.
 */
import type { JSX } from 'react';
import type { BundledPromoArt } from '../promos/promos';

const PRIMARY = 'var(--apipay-color-primary, #3a4fd9)';
const PRIMARY_SOFT = `color-mix(in srgb, ${PRIMARY} 12%, white)`;
const PRIMARY_DEEP = `color-mix(in srgb, ${PRIMARY} 82%, black)`;

/**
 * Las ilustraciones son decorativas: el texto real de la promocion viaja en el `alt` del contenedor,
 * que es lo que lee un lector de pantalla. Por eso cada SVG va con `aria-hidden` y sin `<title>`:
 * anunciarlo dos veces es peor que no anunciarlo.
 */
const COMMON_SVG_PROPS = {
  viewBox: '0 0 320 200',
  role: 'presentation' as const,
  'aria-hidden': true,
  preserveAspectRatio: 'xMidYMid slice',
  className: 'h-full w-full',
};

function Envio(): JSX.Element {
  return (
    <svg {...COMMON_SVG_PROPS}>
      <rect width="320" height="200" fill={PRIMARY_SOFT} />
      <circle cx="268" cy="34" r="72" fill={PRIMARY} opacity="0.10" />
      {/* Caja */}
      <rect x="36" y="96" width="88" height="62" rx="6" fill={PRIMARY} opacity="0.9" />
      <path d="M36 118h88" stroke="white" strokeWidth="4" opacity="0.6" />
      <rect x="70" y="96" width="20" height="62" fill="white" opacity="0.35" />
      {/* Lineas de velocidad */}
      <path
        d="M140 112h56M140 130h84M140 148h40"
        stroke={PRIMARY_DEEP}
        strokeWidth="6"
        strokeLinecap="round"
        opacity="0.35"
      />
      <text x="36" y="62" fill={PRIMARY_DEEP} fontSize="30" fontWeight="700">
        Envío gratis
      </text>
      <text x="36" y="84" fill={PRIMARY_DEEP} fontSize="15" opacity="0.75">
        en compras sobre $30.000
      </text>
    </svg>
  );
}

function Cuotas(): JSX.Element {
  return (
    <svg {...COMMON_SVG_PROPS}>
      <rect width="320" height="200" fill={PRIMARY} />
      <circle cx="40" cy="176" r="88" fill="white" opacity="0.08" />
      <circle cx="290" cy="24" r="60" fill="white" opacity="0.08" />
      {/* Tarjetas escalonadas */}
      <rect x="188" y="104" width="96" height="60" rx="8" fill="white" opacity="0.25" />
      <rect x="176" y="94" width="96" height="60" rx="8" fill="white" opacity="0.45" />
      <rect x="164" y="84" width="96" height="60" rx="8" fill="white" />
      <rect x="176" y="98" width="34" height="8" rx="4" fill={PRIMARY} opacity="0.5" />
      <rect x="176" y="118" width="60" height="6" rx="3" fill={PRIMARY} opacity="0.25" />
      <text x="28" y="76" fill="white" fontSize="46" fontWeight="800">
        12
      </text>
      <text x="28" y="104" fill="white" fontSize="18" fontWeight="600">
        cuotas
      </text>
      <text x="28" y="128" fill="white" fontSize="14" opacity="0.85">
        sin interés
      </text>
    </svg>
  );
}

function Devolucion(): JSX.Element {
  return (
    <svg {...COMMON_SVG_PROPS}>
      <rect width="320" height="200" fill="white" />
      <rect width="320" height="200" fill={PRIMARY} opacity="0.06" />
      {/*
        La flecha es MARCA DE AGUA, no un elemento al lado del texto.
        Dos intentos previos la pusieron como figura de primer plano —primero centrada, luego
        desplazada y mas pequena— y las dos veces se comio el final de «30 dias»: en una caja de 16:10
        no caben un titulo de 34px y un glifo de 80 sin pelearse. Como fondo al 14% no puede colisionar
        con nada, aunque el texto crezca o se traduzca a un idioma mas largo, y ademas se sale del
        recuadro a proposito para que se lea como textura y no como icono.
      */}
      <g opacity="0.14">
        <path
          d="M300 118a62 62 0 1 1-19-45"
          fill="none"
          stroke={PRIMARY}
          strokeWidth="18"
          strokeLinecap="round"
        />
        <path
          d="M281 54v34h-34"
          fill="none"
          stroke={PRIMARY}
          strokeWidth="18"
          strokeLinecap="round"
        />
      </g>
      {/* Icono pequeno, separado del titulo y sin tocarlo: da el simbolo sin robar espacio. */}
      <g transform="translate(32 34)">
        <circle cx="13" cy="13" r="13" fill={PRIMARY} opacity="0.12" />
        <path
          d="M19 13a6 6 0 1 1-2-4.4"
          fill="none"
          stroke={PRIMARY}
          strokeWidth="2.4"
          strokeLinecap="round"
        />
        <path d="M17 5v3.4h-3.4" fill="none" stroke={PRIMARY} strokeWidth="2.4" strokeLinecap="round" />
      </g>
      <text x="32" y="104" fill={PRIMARY_DEEP} fontSize="36" fontWeight="800">
        30 días
      </text>
      <text x="32" y="132" fill={PRIMARY_DEEP} fontSize="16" opacity="0.8">
        para devolver, sin preguntas
      </text>
    </svg>
  );
}

const ART: Record<BundledPromoArt, () => JSX.Element> = {
  envio: Envio,
  cuotas: Cuotas,
  devolucion: Devolucion,
};

/**
 * Pinta la ilustracion de una promocion.
 *
 * <p>Si el nombre no corresponde a ninguna —solo posible si alguien anade un valor al tipo y olvida
 * la ilustracion— devuelve `null` y el carrusel se queda sin esa lamina, en vez de romper el render
 * de la pantalla de pago.
 */
export function PromoArt({ art }: { readonly art: BundledPromoArt }): JSX.Element | null {
  const Component = ART[art];
  return Component === undefined ? null : <Component />;
}
